import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { onboardingAPI, usersAPI } from '@/services/api';
import { toast } from 'sonner';
import {
  FolderOpen, Clock, Receipt, LayoutDashboard,
  Users, Smartphone, ArrowRight, ArrowLeft,
  ChevronRight, CheckCircle2,
  ClipboardList, FileText, Download, BookOpen,
  Mail, Plus, X, Send, Loader2
} from 'lucide-react';

const Welcome = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const startTime = useRef(Date.now());

  const role = user?.role || 'owner';
  const userId = user?.user_id;
  const storageKey = userId ? `rektra_onboarding_${userId}` : 'rektra_onboarding_complete';

  const isOwnerAdmin = ['owner', 'admin'].includes(role);
  const isManager = role === 'manager';
  const isWorker = role === 'worker';
  const isBookkeeper = role === 'bookkeeper';

  const TOTAL_STEPS = isOwnerAdmin ? 1 : 3;

  const elapsed = () => Math.round((Date.now() - startTime.current) / 1000);

  useEffect(() => {
    onboardingAPI.logEvent({ event_type: 'started', step: 0 });
    try { sessionStorage.removeItem('rektra_just_registered'); } catch { /* private mode */ }
  }, []);

  const complete = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    onboardingAPI.logEvent({ event_type: 'completed', step: TOTAL_STEPS - 1, time_spent_seconds: elapsed() });
    navigate('/dashboard', { replace: true });
  }, [navigate, storageKey, TOTAL_STEPS]);

  const skip = useCallback(() => {
    localStorage.setItem(storageKey, 'true');
    onboardingAPI.logEvent({ event_type: 'skipped', step, time_spent_seconds: elapsed() });
    navigate('/dashboard', { replace: true });
  }, [navigate, storageKey, step]);

  const next = () => setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const handleFirstAction = (path) => {
    localStorage.setItem(storageKey, 'true');
    const actionMap = { '/projects': 'view_projects', '/time': 'log_hours', '/receipts/new': 'upload_receipt', '/receipts': 'view_receipts', '/reports/hours': 'hours_overview', '/reports': 'export_data' };
    onboardingAPI.logEvent({ event_type: 'first_action', first_action: actionMap[path] || path, time_spent_seconds: elapsed() });
    navigate(path);
  };

  const handleViewExample = () => {
    // Intro is one screen now (iter323), so treat "see the example" as
    // finishing onboarding. Exiting the demo then returns to the dashboard
    // instead of dropping the user back on intro step 1.
    localStorage.setItem(storageKey, 'true');
    onboardingAPI.logEvent({ event_type: 'first_action', first_action: 'view_example', time_spent_seconds: elapsed() });
    sessionStorage.setItem('example_from', '/dashboard');
    navigate('/projects/example');
  };

  const handleCreateFirstProject = () => {
    localStorage.setItem(storageKey, 'true');
    onboardingAPI.logEvent({ event_type: 'first_action', first_action: 'create_project', time_spent_seconds: elapsed() });
    navigate('/projects?new=1');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="welcome-page" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="flex justify-end px-4 pt-3 pb-1">
        <button
          onClick={skip}
          className="text-sm text-foreground-muted hover:text-foreground transition-colors py-2 px-3"
          data-testid="onboarding-skip"
        >
          {t('onboarding_skip')}
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-lg">

          {/* ═══════════ OWNER / ADMIN — one screen (iter323) ═══════════
              Was 6 screens (value props x3, demo, invite team, pick an
              action). Invite-team moved to a dismissible dashboard card
              and "add to home screen" to the existing PWA card. */}
          {isOwnerAdmin && (
            <div className="text-center" data-testid="welcome-owner-single">
              <div className="w-14 h-14 bg-card-hover rounded-2xl flex items-center justify-center mx-auto mb-5">
                <img
                  src="/logos/favicon-black.png"
                  alt="REKTRA"
                  className="w-9 h-9 object-contain"
                  data-testid="welcome-logo"
                />
              </div>
              <h1 className="text-2xl font-semibold mb-2" data-testid="welcome-heading">
                {t('onboarding_welcome_heading')}
              </h1>
              <p className="text-sm text-foreground-secondary mb-8 max-w-sm mx-auto">
                {t('onboarding_welcome_line')}
              </p>
              <button
                onClick={handleCreateFirstProject}
                className="btn btn-primary btn-lg w-full"
                data-testid="welcome-create-project"
              >
                <Plus size={18} />
                {t('onboarding_welcome_primary')}
              </button>
              <button
                onClick={handleViewExample}
                className="btn btn-secondary btn-lg w-full mt-3"
                data-testid="welcome-view-example"
              >
                <BookOpen size={18} />
                {t('onboarding_welcome_secondary')}
              </button>
            </div>
          )}
          {false && (
            <>
              {step === 0 && <OwnerStep0 t={t} next={next} />}
              {step === 1 && <OwnerStep1 t={t} next={next} back={back} />}
              {step === 2 && <OwnerStep2 t={t} next={next} back={back} />}
              {step === 3 && <DemoStep t={t} next={next} back={back} onViewExample={handleViewExample} />}
              {step === 4 && <InviteTeamStep t={t} next={next} back={back} />}
              {step === 5 && (
                <ActionStep
                  t={t} back={back} complete={complete}
                  handleFirstAction={handleFirstAction}
                  actions={[
                    { icon: <FolderOpen size={20} className="text-accent" />, label: t('onboarding_v2_action_project'), path: '/projects', primary: true, testId: 'onboarding-action-project' },
                    { icon: <Clock size={20} className="text-accent" />, label: t('onboarding_v2_action_hours'), path: '/time', testId: 'onboarding-action-hours' },
                    { icon: <Receipt size={20} className="text-accent" />, label: t('onboarding_v2_action_receipt'), path: '/receipts/new', testId: 'onboarding-action-receipt' },
                  ]}
                />
              )}
            </>
          )}

          {/* ═══════════ MANAGER ═══════════ */}
          {isManager && (
            <>
              {step === 0 && <ManagerStep0 t={t} next={next} />}
              {step === 1 && <ManagerStep1 t={t} next={next} back={back} />}
              {step === 2 && (
                <ActionStep
                  t={t} back={back} complete={complete}
                  handleFirstAction={handleFirstAction}
                  actions={[
                    { icon: <FolderOpen size={20} className="text-accent" />, label: t('onboarding_mgr_action_projects'), path: '/projects', primary: true, testId: 'onboarding-action-projects' },
                    { icon: <Clock size={20} className="text-accent" />, label: t('onboarding_v2_action_hours'), path: '/time', testId: 'onboarding-action-hours' },
                    { icon: <Receipt size={20} className="text-accent" />, label: t('onboarding_v2_action_receipt'), path: '/receipts/new', testId: 'onboarding-action-receipt' },
                  ]}
                />
              )}
            </>
          )}

          {/* ═══════════ WORKER ═══════════ */}
          {isWorker && (
            <>
              {step === 0 && <WorkerStep0 t={t} next={next} />}
              {step === 1 && <WorkerStep1 t={t} next={next} back={back} />}
              {step === 2 && (
                <ActionStep
                  t={t} back={back} complete={complete}
                  handleFirstAction={handleFirstAction}
                  actions={[
                    { icon: <FolderOpen size={20} className="text-accent" />, label: t('onboarding_wkr_action_projects'), path: '/projects', primary: true, testId: 'onboarding-action-projects' },
                    { icon: <Clock size={20} className="text-accent" />, label: t('onboarding_wkr_action_hours'), path: '/time', testId: 'onboarding-action-hours' },
                  ]}
                />
              )}
            </>
          )}

          {/* ═══════════ BOOKKEEPER ═══════════ */}
          {isBookkeeper && (
            <>
              {step === 0 && <BookkeeperStep0 t={t} next={next} />}
              {step === 1 && <BookkeeperStep1 t={t} next={next} back={back} />}
              {step === 2 && (
                <ActionStep
                  t={t} back={back} complete={complete}
                  handleFirstAction={handleFirstAction}
                  actions={[
                    { icon: <Receipt size={20} className="text-accent" />, label: t('onboarding_bkp_action_receipts'), path: '/receipts', primary: true, testId: 'onboarding-action-receipts' },
                    { icon: <Clock size={20} className="text-accent" />, label: t('onboarding_bkp_action_hours'), path: '/reports/hours', testId: 'onboarding-action-hours' },
                    { icon: <Download size={20} className="text-accent" />, label: t('onboarding_bkp_action_export'), path: '/reports', testId: 'onboarding-action-export' },
                  ]}
                />
              )}
            </>
          )}

          {/* Step dots */}
          <div className="flex justify-center gap-2 mt-8" data-testid="onboarding-dots">
            {Array.from({ length: isOwnerAdmin ? 0 : TOTAL_STEPS }).map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === step ? 'bg-accent w-6' : 'bg-foreground/15 hover:bg-foreground/25'
                }`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   OWNER / ADMIN STEPS
   ══════════════════════════════════════════ */

const OwnerStep0 = ({ t, next }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-0">
    <div className="text-center mb-2">
      <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5">
        <img src="/logos/app-icon-transparent.png" alt="Rektra" className="w-12 h-12 object-contain" />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_v2_welcome_title')}</h1>
    </div>
    <p className="text-sm text-foreground-secondary text-center">{t('onboarding_v2_welcome_subtitle')}</p>
    <div className="space-y-2.5">
      <BenefitRow icon={<FolderOpen size={18} />} text={t('onboarding_v2_benefit_projects')} />
      <BenefitRow icon={<Clock size={18} />} text={t('onboarding_v2_benefit_hours')} />
      <BenefitRow icon={<Receipt size={18} />} text={t('onboarding_v2_benefit_receipts')} />
      <BenefitRow icon={<LayoutDashboard size={18} />} text={t('onboarding_v2_benefit_costs')} />
    </div>
    <p className="text-xs text-foreground-muted text-center">{t('onboarding_v2_welcome_one_place')}</p>
    <div className="card p-4 bg-card/50 border-border/50">
      <p className="text-xs text-foreground-muted mb-2">{t('onboarding_v2_replaces_title')}</p>
      <p className="text-sm text-foreground-secondary">{t('onboarding_v2_replaces_list')}</p>
    </div>
    <button onClick={next} className="w-full btn btn-primary btn-lg" data-testid="onboarding-next">
      {t('onboarding_v2_next')} <ArrowRight size={18} />
    </button>
  </div>
);

const OwnerStep1 = ({ t, next, back }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-1">
    <div className="text-center mb-2">
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_v2_workflow_title')}</h1>
    </div>
    <div className="card p-5">
      <p className="text-xs text-foreground-muted mb-3 font-medium uppercase tracking-wide">{t('onboarding_v2_workflow_subtitle')}</p>
      <div className="space-y-3">
        <FlowStep num="1" text={t('onboarding_v2_flow_1')} />
        <FlowStep num="2" text={t('onboarding_v2_flow_2')} />
        <FlowStep num="3" text={t('onboarding_v2_flow_3')} />
        <FlowStep num="4" text={t('onboarding_v2_flow_4')} last />
      </div>
    </div>
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Users size={16} className="text-accent" />
        <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted">{t('onboarding_v2_roles_title')}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm font-medium mb-1.5">{t('onboarding_v2_role_worker')}</p>
          <ul className="space-y-1 text-xs text-foreground-secondary">
            <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_v2_worker_1')}</li>
            <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_v2_worker_2')}</li>
          </ul>
        </div>
        <div>
          <p className="text-sm font-medium mb-1.5">{t('onboarding_v2_role_owner')}</p>
          <ul className="space-y-1 text-xs text-foreground-secondary">
            <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_v2_owner_1')}</li>
            <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_v2_owner_2')}</li>
            <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_v2_owner_3')}</li>
          </ul>
        </div>
      </div>
    </div>
    <NavButtons back={back} next={next} t={t} />
  </div>
);

const OwnerStep2 = ({ t, next, back }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-2">
    <div className="text-center mb-2">
      <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Smartphone className="text-accent" size={28} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_v2_mobile_title')}</h1>
      <p className="text-sm text-foreground-secondary">{t('onboarding_v2_mobile_subtitle')}</p>
    </div>
    <div className="space-y-2.5">
      <BenefitRow icon={<Clock size={18} />} text={t('onboarding_v2_mobile_1')} />
      <BenefitRow icon={<Receipt size={18} />} text={t('onboarding_v2_mobile_2')} />
      <BenefitRow icon={<Smartphone size={18} />} text={t('onboarding_v2_mobile_3')} />
    </div>
    <p className="text-xs text-foreground-muted text-center">{t('onboarding_v2_mobile_time')}</p>
    <NavButtons back={back} next={next} t={t} />
  </div>
);

/* ══════════════════════════════════════════
   MANAGER STEPS
   ══════════════════════════════════════════ */

const ManagerStep0 = ({ t, next }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-0">
    <div className="text-center mb-2">
      <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Users className="text-accent" size={28} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_mgr_step0_title')}</h1>
    </div>
    <p className="text-sm text-foreground-secondary text-center">{t('onboarding_mgr_step0_text')}</p>
    <div className="card p-5">
      <p className="text-xs text-foreground-muted mb-3 font-medium">{t('onboarding_mgr_step0_can')}</p>
      <div className="space-y-2.5">
        <BenefitRow icon={<FolderOpen size={18} />} text={t('onboarding_mgr_step0_l1')} />
        <BenefitRow icon={<Clock size={18} />} text={t('onboarding_mgr_step0_l2')} />
        <BenefitRow icon={<Receipt size={18} />} text={t('onboarding_mgr_step0_l3')} />
        <BenefitRow icon={<CheckCircle2 size={18} />} text={t('onboarding_mgr_step0_l4')} />
      </div>
    </div>
    <button onClick={next} className="w-full btn btn-primary btn-lg" data-testid="onboarding-next">
      {t('onboarding_v2_next')} <ArrowRight size={18} />
    </button>
  </div>
);

const ManagerStep1 = ({ t, next, back }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-1">
    <div className="text-center mb-2">
      <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <ClipboardList className="text-accent" size={28} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_mgr_step1_title')}</h1>
    </div>
    <div className="card p-5">
      <p className="text-xs text-foreground-muted mb-3 font-medium">{t('onboarding_mgr_step1_text')}</p>
      <div className="space-y-2.5">
        <BenefitRow icon={<Clock size={18} />} text={t('onboarding_mgr_step1_l1')} />
        <BenefitRow icon={<Receipt size={18} />} text={t('onboarding_mgr_step1_l2')} />
        <BenefitRow icon={<ClipboardList size={18} />} text={t('onboarding_mgr_step1_l3')} />
        <BenefitRow icon={<FileText size={18} />} text={t('onboarding_mgr_step1_l4')} />
      </div>
    </div>
    <div className="card p-4 bg-card/50 border-border/50">
      <p className="text-sm text-foreground-secondary">{t('onboarding_mgr_step1_jobs')}</p>
      <p className="text-xs text-foreground-muted mt-1">{t('onboarding_mgr_step1_workers')}</p>
    </div>
    <NavButtons back={back} next={next} t={t} />
  </div>
);

/* ══════════════════════════════════════════
   WORKER STEPS
   ══════════════════════════════════════════ */

const WorkerStep0 = ({ t, next }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-0">
    <div className="text-center mb-2">
      <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Clock className="text-accent" size={28} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_wkr_step0_title')}</h1>
    </div>
    <p className="text-sm text-foreground-secondary text-center">{t('onboarding_wkr_step0_text')}</p>
    <div className="card p-5">
      <p className="text-xs text-foreground-muted mb-3 font-medium">{t('onboarding_wkr_step0_role')}</p>
      <div className="space-y-2.5">
        <BenefitRow icon={<Clock size={18} />} text={t('onboarding_wkr_step0_l1')} />
        <BenefitRow icon={<CheckCircle2 size={18} />} text={t('onboarding_wkr_step0_l2')} />
        <BenefitRow icon={<Receipt size={18} />} text={t('onboarding_wkr_step0_l3')} />
      </div>
    </div>
    <button onClick={next} className="w-full btn btn-primary btn-lg" data-testid="onboarding-next">
      {t('onboarding_v2_next')} <ArrowRight size={18} />
    </button>
  </div>
);

const WorkerStep1 = ({ t, next, back }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-1">
    <div className="text-center mb-2">
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_wkr_step1_title')}</h1>
    </div>
    <div className="space-y-3">
      <FlowStep num="1" text={t('onboarding_wkr_step1_l1')} />
      <FlowStep num="2" text={t('onboarding_wkr_step1_l2')} />
      <FlowStep num="3" text={t('onboarding_wkr_step1_l3')} last />
    </div>
    <NavButtons back={back} next={next} t={t} />
  </div>
);

/* ══════════════════════════════════════════
   BOOKKEEPER STEPS
   ══════════════════════════════════════════ */

const BookkeeperStep0 = ({ t, next }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-0">
    <div className="text-center mb-2">
      <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <BookOpen className="text-accent" size={28} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_bkp_step0_title')}</h1>
    </div>
    <p className="text-sm text-foreground-secondary text-center">{t('onboarding_bkp_step0_text')}</p>
    <div className="card p-5">
      <div className="space-y-2.5">
        <BenefitRow icon={<Receipt size={18} />} text={t('onboarding_bkp_step0_l1')} />
        <BenefitRow icon={<LayoutDashboard size={18} />} text={t('onboarding_bkp_step0_l2')} />
        <BenefitRow icon={<Download size={18} />} text={t('onboarding_bkp_step0_l3')} />
      </div>
    </div>
    <button onClick={next} className="w-full btn btn-primary btn-lg" data-testid="onboarding-next">
      {t('onboarding_v2_next')} <ArrowRight size={18} />
    </button>
  </div>
);

const BookkeeperStep1 = ({ t, next, back }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-1">
    <div className="text-center mb-2">
      <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <FileText className="text-accent" size={28} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_bkp_step1_title')}</h1>
    </div>
    <div className="space-y-3">
      <FlowStep num="1" text={t('onboarding_bkp_step1_l1')} />
      <FlowStep num="2" text={t('onboarding_bkp_step1_l2')} />
      <FlowStep num="3" text={t('onboarding_bkp_step1_l3')} last />
    </div>
    <NavButtons back={back} next={next} t={t} />
  </div>
);

/* ══════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════ */

const DemoStep = ({ t, next, back, onViewExample }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-demo">
    <div className="text-center mb-2">
      <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <BookOpen className="text-accent" size={32} />
      </div>
      <h1 className="text-2xl font-bold mb-2">
        {t('onboarding_demo_title')}
      </h1>
      <p className="text-sm text-foreground-secondary max-w-sm mx-auto">
        {t('onboarding_demo_subtitle')}
      </p>
    </div>

    <button
      onClick={onViewExample}
      className="w-full btn btn-primary btn-lg"
      data-testid="onboarding-enter-demo"
    >
      <FileText size={18} />
      {t('onboarding_demo_button')}
      <ArrowRight size={18} />
    </button>

    <div className="card p-4 bg-card/50 border-border/50">
      <div className="space-y-2.5">
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <ClipboardList size={11} className="text-accent" />
          </div>
          <p className="text-xs text-foreground-secondary">
            {t('onboarding_demo_bullet_project')}
          </p>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Receipt size={11} className="text-accent" />
          </div>
          <p className="text-xs text-foreground-secondary">
            {t('onboarding_demo_bullet_receipts')}
          </p>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Download size={11} className="text-accent" />
          </div>
          <p className="text-xs text-foreground-secondary">
            {t('onboarding_demo_bullet_invoice')}
          </p>
        </div>
      </div>
    </div>

    <div className="flex gap-3">
      <button onClick={back} className="btn btn-secondary flex-shrink-0" data-testid="onboarding-back">
        <ArrowLeft size={16} />
      </button>
      <button onClick={next} className="flex-1 btn btn-secondary" data-testid="onboarding-skip-demo">
        {t('onboarding_demo_skip')} <ArrowRight size={16} />
      </button>
    </div>
  </div>
);

const ActionStep = ({ t, back, complete, handleFirstAction, actions }) => (
  <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-action">
    <div className="text-center mb-2">
      <h1 className="text-2xl font-bold mb-2">{t('onboarding_v2_action_title')}</h1>
      <p className="text-sm text-foreground-secondary">{t('onboarding_v2_action_subtitle')}</p>
    </div>
    <div className="space-y-3">
      {actions.map((a) => (
        <ActionCard
          key={a.testId}
          icon={a.icon}
          title={a.label}
          primary={a.primary}
          onClick={() => handleFirstAction(a.path)}
          testId={a.testId}
        />
      ))}
    </div>
    {/* Næsta skref guidance */}
    <div className="card p-4 bg-card/50 border-border/50">
      <p className="text-xs font-medium text-foreground-muted mb-1.5">{t('onboarding_next_step_title')}</p>
      <ul className="space-y-1 text-xs text-foreground-secondary">
        <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_next_step_use')}</li>
        <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />{t('onboarding_next_step_help')}</li>
      </ul>
    </div>
    <div className="flex gap-3">
      <button onClick={back} className="btn btn-secondary flex-shrink-0" data-testid="onboarding-back">
        <ArrowLeft size={16} />
      </button>
      <button onClick={complete} className="flex-1 btn btn-secondary" data-testid="onboarding-go-dashboard">
        {t('onboarding_v2_go_dashboard')} <ArrowRight size={16} />
      </button>
    </div>
  </div>
);

const NavButtons = ({ back, next, t }) => (
  <div className="flex gap-3">
    <button onClick={back} className="btn btn-secondary flex-shrink-0" data-testid="onboarding-back">
      <ArrowLeft size={16} />
    </button>
    <button onClick={next} className="flex-1 btn btn-primary btn-lg" data-testid="onboarding-next">
      {t('onboarding_v2_next')} <ArrowRight size={18} />
    </button>
  </div>
);

/* ══════════════════════════════════════════
   INVITE TEAM STEP (owner/admin only)
   ══════════════════════════════════════════ */

const ROLE_OPTIONS = [
  { value: 'worker', tk: 'onboarding_invite_role_worker' },
  { value: 'manager', tk: 'onboarding_invite_role_manager' },
  { value: 'admin', tk: 'onboarding_invite_role_admin' },
  { value: 'bookkeeper', tk: 'onboarding_invite_role_bookkeeper' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteTeamStep = ({ t, next, back }) => {
  const [rows, setRows] = useState([{ email: '', role: 'worker' }]);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  const addRow = () => setRows(r => [...r, { email: '', role: 'worker' }]);
  const removeRow = (idx) => setRows(r => (r.length === 1 ? r : r.filter((_, i) => i !== idx)));
  const updateRow = (idx, patch) =>
    setRows(r => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const validRows = rows
    .map(r => ({ ...r, email: r.email.trim().toLowerCase() }))
    .filter(r => EMAIL_RE.test(r.email));

  const handleSend = async () => {
    if (validRows.length === 0) {
      next();
      return;
    }
    setSending(true);
    let successes = 0;
    for (const row of validRows) {
      try {
        await usersAPI.invite(row);
        successes += 1;
      } catch (e) {
        toast.error(`${row.email}: ${e?.response?.data?.detail || t('onboarding_invite_error')}`);
      }
    }
    setSending(false);
    setSentCount(successes);
    if (successes > 0) {
      const key = successes === 1 ? 'onboarding_invite_sent_count_one' : 'onboarding_invite_sent_count_many';
      toast.success(t(key).replace('{count}', String(successes)));
      onboardingAPI.logEvent({ event_type: 'invite_sent', metadata: { count: successes } });
      next();
    }
  };

  const canSend = validRows.length > 0 && !sending;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-step-invite">
      <div className="text-center mb-2">
        <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Users className="text-accent" size={28} />
        </div>
        <h1 className="text-2xl font-bold mb-2">{t('onboarding_invite_title')}</h1>
        <p className="text-sm text-foreground-secondary">{t('onboarding_invite_subtitle')}</p>
      </div>

      <div className="space-y-2.5" data-testid="onboarding-invite-rows">
        {rows.map((row, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2"
            data-testid={`onboarding-invite-row-${idx}`}
          >
            <div className="flex-1 relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="email"
                value={row.email}
                onChange={(e) => updateRow(idx, { email: e.target.value })}
                placeholder={t('onboarding_invite_email_placeholder')}
                className="pl-9 w-full"
                autoComplete="off"
                data-testid={`onboarding-invite-email-${idx}`}
              />
            </div>
            <select
              value={row.role}
              onChange={(e) => updateRow(idx, { role: e.target.value })}
              className="w-32"
              data-testid={`onboarding-invite-role-${idx}`}
            >
              {ROLE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(o.tk)}</option>
              ))}
            </select>
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="p-2 text-foreground-muted hover:text-error transition-colors"
                aria-label="Remove"
                data-testid={`onboarding-invite-remove-${idx}`}
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="text-sm text-accent hover:text-accent/80 transition-colors inline-flex items-center gap-1.5"
        data-testid="onboarding-invite-add-row"
      >
        <Plus size={14} /> {t('onboarding_invite_add_another')}
      </button>

      <p className="text-xs text-foreground-muted">{t('onboarding_invite_hint')}</p>

      <div className="flex gap-3">
        <button onClick={back} className="btn btn-secondary flex-shrink-0" data-testid="onboarding-back">
          <ArrowLeft size={16} />
        </button>
        <button
          onClick={next}
          className="flex-1 btn btn-secondary"
          data-testid="onboarding-invite-skip"
        >
          {t('onboarding_invite_skip')}
        </button>
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="flex-1 btn btn-primary"
          data-testid="onboarding-invite-send"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {sending ? t('onboarding_invite_sending') : t('onboarding_invite_send')}
        </button>
      </div>
      {sentCount > 0 && (
        <p className="text-xs text-accent text-center" data-testid="onboarding-invite-sent-summary">
          {(sentCount === 1 ? t('onboarding_invite_sent_count_one') : t('onboarding_invite_sent_count_many'))
            .replace('{count}', String(sentCount))}
        </p>
      )}
    </div>
  );
};

const BenefitRow = ({ icon, text }) => (
  <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border/50">
    <span className="text-accent flex-shrink-0">{icon}</span>
    <span className="text-sm">{text}</span>
  </div>
);

const FlowStep = ({ num, text, last }) => (
  <div className="flex items-center gap-3">
    <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
      <span className="text-xs font-bold text-accent">{num}</span>
    </div>
    <span className="text-sm">{text}</span>
    {!last && <ChevronRight size={14} className="text-foreground-muted ml-auto flex-shrink-0" />}
  </div>
);

const ActionCard = ({ icon, title, primary, subtle, onClick, disabled, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all ${
      primary
        ? 'bg-accent/10 border-2 border-accent/30 hover:border-accent/50'
        : subtle
          ? 'bg-card border border-border/50 hover:border-border text-foreground-muted'
          : 'bg-card border border-border hover:border-accent/30'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    data-testid={testId}
  >
    <div className="w-10 h-10 bg-background rounded-xl flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <span className={`text-sm font-medium ${primary ? 'text-foreground' : ''}`}>{title}</span>
    <ChevronRight size={16} className="ml-auto text-foreground-muted flex-shrink-0" />
  </button>
);

export default Welcome;
